import { TranslocoService } from '@jsverse/transloco';
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter,
  Inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation
} from '@angular/core';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';

import { isNullOrUndefined } from '@app/util/util';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import {
  SearchBarCalendarAbstractComponent
} from '@shared/base-search-bar/components/calendar/abstract/search-bar-calendar.abstract';
import { environment } from '@environment';
import { LOCALES } from '@app/constants/locales.constanst';
import { FlightType } from '@search-bar/entities/flight-type';
import { SearchType } from '@search-bar/entities/search-type';
import { AppFacade } from '@app/app.facade';
import {
  DateCalendarDefaultDates, DateCalendarField, DateCalendarSelectedEvent
} from '@design-system/components/organisms/date-calendar/entities/date-calendar.types';
import { allowSameDaySelection, daysOrNightsNumberLabel } from '@shared/base-search-bar/mappers/search-bar-calendar.mapper';

@Component({
  selector: 'app-search-bar-calendar',
  templateUrl: './search-bar-calendar.component.html',
  styleUrls: ['./search-bar-calendar.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SearchBarCalendarComponent extends SearchBarCalendarAbstractComponent implements OnInit, OnChanges {

  @Input() isOneWayTrip = false;
  @Input() locale: Locale = LOCALES[environment.language.toLocaleLowerCase()];
  @Input() show: boolean = false;
  @Input() format: string = 'E, d MMM';
  @Input() isHotelDetail?: boolean = false;
  @Input() arrivalDate: Date;
  @Input() departureDate: Date;
  @Input() activeType: CalendarField;
  @Input() departurePlaceholder: string;
  @Input() returnPlaceholder: string;
  @Input() flightTypeCalendar: FlightType;
  @Input() searchType: SearchType;
  @Input() fieldActivated: DateCalendarField;

  @Output() emitApplySelection: EventEmitter<Date[]> = new EventEmitter<Date[]>();
  @Output() flightType: EventEmitter<string> = new EventEmitter<string>();

  public daysBetweenTwoDates = 0;
  public translate = 0;

  constructor(
    protected facade: AppFacade,
    protected translateService: TranslocoService,
    protected changeDetectorRef: ChangeDetectorRef,
    @Inject(IS_BROWSER) public isBrowser: boolean,
  ) {
    super(changeDetectorRef, facade, translateService);
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.facade.user$.subscribe(user => {
        this.user = user;
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isBrowser) {
      if (this.show) {
        this.toggleShow(true);
      }
    }
  }

  get arrivalText(): string {
    return !isNullOrUndefined(this.arrivalDate) ? this.returnPlaceholder : 'DATE';
  }

  get departureText(): string {
    return !isNullOrUndefined(this.departureDate) ? this.departurePlaceholder : 'DATE';
  }

  get daysBetweenTwoDatesLabel(): string {
    return this.daysBetweenTwoDatesLabelAbstract(this.daysBetweenTwoDates, this.searchType);
  }

  get isValidDateCalendar(): boolean {
    return this.isOneWayTrip ? !isNullOrUndefined(this.departureDate) :
      !isNullOrUndefined(this.departureDate) && !isNullOrUndefined(this.arrivalDate);
  }

  get minDate(): number {
    const disabledDaysByUserConfig = this.calendarDaysLimit(
      this.searchType, this.user?.id, this.user?.is_prime, this.isDiamondUser(this.user), this.user.has_rappicard,
    );
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date.setDate(date.getDate() + disabledDaysByUserConfig);
  }

  get defaultDates(): DateCalendarDefaultDates {
    return [this.departureDate ?? null, this.arrivalDate ?? null];
  }

  get allowTheSameDay(): boolean {
    return allowSameDaySelection(this.searchType);
  }

  public translateEvent(translate: number): void {
    this.translate = translate;
  }

  public selectEvent(dates: DateCalendarSelectedEvent): void {
    this.daysBetweenTwoDates = daysOrNightsNumberLabel(this.searchType, dates?.days, dates?.nights);
    this.emitApplySelection.emit([dates?.left?.date ?? null, dates?.right?.date ?? null]);
    this.changeDetectorRef.detectChanges();
  }

  public close(): void {
    this.daysBetweenTwoDates = 0;
    this.toggleShow(false);
    this.changeDetectorRef.detectChanges();
  }

  public toggleShow(status: boolean = !this.showCalendar): void {
    this.showCalendar = status;
  }

  public selectFlightType(e: string): void {
    this.flightType.emit(e);
  }
}
